import { Instance, SnapshotIn, types, castToSnapshot, getRoot } from 'mobx-state-tree';
import { cleanFeature, escapeRegExp } from '@services/utils';
import { computeSeedAmountPerUnitArea, isPresent, luxonUserLocale } from '@farmai-shared/LFUtils';
import { SupportedErrorTexts } from './enums';
import { GeoJSON, IPolygon } from './GeoJSON';
import { Feature as olFeature } from 'ol';
import { Polygon } from 'ol/geom';
import { DateTime } from 'luxon';
import { withDateProcessors } from './DateTimeUtility';
import { IRootStoreAny } from './rootUtilType';
import { seedUnitList } from '@services/next-i18n';
import { LFDrawMode, LFMapGeoJSON } from '@app/_components/lf-map/mapTypes';
import { v4 as uuidv4 } from 'uuid';
export const Feature = withDateProcessors(GeoJSON.named('Feature').views(self => ({
  get root() {
    return getRoot<IRootStoreAny>(self);
  }
})).views(self => ({
  get crop() {
    return self.properties?.get('cropCanonical') || self.properties?.get('crop');
  },
  get importedCrop() {
    return self.properties?.get('crop');
  },
  get plantingDate() {
    return self.properties?.get('plantingDate');
  },
  get amountOfSeeds() {
    return self.properties?.get('amountOfSeeds');
  },
  get amountOfSeeds_unit() {
    return self.properties?.get('amountOfSeeds_unit');
  },
  get plantingDateZ() {
    const lang = self.root.userStore.user.settings.lang;
    const dt = self.properties.get('plantingDate');
    return isPresent(dt) && dt instanceof DateTime ? self.properties.get('plantingDate').setZone(self.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang)) : null;
  },
  get tz() {
    return self.properties.get('timezone');
  },
  get canRemovePoint() {
    return self.getPolygon?.canRemovePoint || false;
  },
  get flatCoords() {
    return self.getPolygon?.flatCoords || [];
  },
  get validCropImport() {
    return isPresent(self.root.cropStore.filterCropFromGeoJSON(self.properties.get('crop')));
  },
  get selected() {
    return self.properties.get('selected');
  },
  get selectedIndexes() {
    return self.properties.get('selectedIndexes');
  }
})).actions(self => ({
  addSelectedIndex(idx: number) {
    !self.selectedIndexes.includes(idx) && self.selectedIndexes.push(idx);
  },
  removeSelectedIndex(idx: number) {
    if (self.selectedIndexes.includes(idx)) {
      const indexToRemove = self.selectedIndexes.indexOf(idx);
      self.selectedIndexes.splice(indexToRemove, 1);
    }
  },
  setCropId(id: string) {
    self.setProperty('cropId', id);
  },
  setCropCanonical(name: string) {
    self.setProperty('cropCanonical', name);
  },
  setSelectedArea(area: number | null) {
    self.setProperty('selectedArea', area);
  },
  update(details: {
    name?: string;
    plantingDate?: string;
    amountOfSeeds: number;
    amountOfSeeds_unit: string;
    cropId?: string;
    cropCanonical?: string;
  }) {
    self.setProperty('name', details.name);
    self.setProperty('plantingDate', details.plantingDate ? DateTime.fromISO(details.plantingDate) : null);
    self.setProperty('cropId', details.cropId);
    self.setProperty('cropCanonical', details.cropCanonical);
    self.setProperty('amountOfSeeds', details.amountOfSeeds);
    self.setProperty('amountOfSeeds_unit', details.amountOfSeeds_unit);
  },
  setFarmId(id: string) {
    self.setProperty('farm_id', id);
  },
  setSelectedForDraw() {
    self.setProperty('selected', true);
  }
})).views(self => ({
  get object() {
    return {
      type: 'Feature',
      geometry: self.getPolygon as IPolygon,
      properties: {
        name: self.name,
        ...(self.plantingDateZ && {
          planting_date: self.plantingDateZ
        }),
        seed_usage: Number(self.amountOfSeeds),
        seed_usage_unit: self.amountOfSeeds_unit?.value || 'bags',
        crop: self.crop,
        farm_id: self.farmId
      }
    };
  },
  get withoutWarning() {
    return self?.plantingDateZ?.isValid && isPresent(self.name) && (isPresent(self.importedCrop) || self.crop !== 'unspecified') && (self.validCropImport || self.crop !== 'unspecified') && self.geometry.hasPolygon && self.getPolygon?.valid;
  },
  get canSave() {
    return isPresent(self.name) && self.geometry.hasPolygon && self.getPolygon?.valid;
  },
  get errorTexts() {
    const texts: SupportedErrorTexts[] = [];
    if (!isPresent(self.name)) texts.push(SupportedErrorTexts.NO_NAME);
    if (!self.geometry.hasPolygon || !self.getPolygon?.valid) texts.push(SupportedErrorTexts.NO_COORDINATES);
    if (!isPresent(self.plantingDateZ)) texts.push(SupportedErrorTexts.NO_DATE);
    if (isPresent(self.plantingDateZ) && !self.plantingDateZ.isValid) texts.push(SupportedErrorTexts.INCORRECT_DATE);
    if (!self.importedCrop && self.crop === 'unspecified') {
      texts.push(SupportedErrorTexts.NO_CROP);
    }
    if (self.importedCrop && !self.validCropImport && self.crop === 'unspecified') {
      texts.push(SupportedErrorTexts.INCORRECT_CROP);
    }
    return texts;
  },
  get area() {
    return self.selectedArea || self.fieldArea;
  }
})).actions(self => ({
  afterCreate() {
    self.setProperty('selected', self.canSave);
  },
  handleSelectAll(isSelectAll: boolean) {
    self.setProperty('selected', self.canSave && isSelectAll);
  }
})).views(self => ({
  get amountOfSeedsComputed() {
    const areaUnit = self.root.userStore.user.settings.area_unit;
    const seeds = self.properties.get('amountOfSeeds');
    return isPresent(seeds) ? computeSeedAmountPerUnitArea(Number(seeds) || 0, self.area || 0, String(areaUnit)) || 0 : 0;
  }
})));
export type IFeature = Instance<typeof Feature>;
export type IFeatureSnapshotIn = SnapshotIn<typeof Feature>;
const transformFromOlFeature = (f: olFeature) => {
  const geo = f.getGeometry() as Polygon;
  return {
    id: f.getId() || uuidv4(),
    geometry: {
      type: 'GeometryCollection',
      geometries: [{
        type: 'Polygon',
        coordinates: geo.getCoordinates()
      }]
    },
    properties: {}
  };
};
export const FeatureStore = types.model({
  features: types.array(Feature),
  selectedFeature: types.maybeNull(types.safeReference(Feature, {
    get(id: string, parent): IFeature {
      return parent?.features?.find(x => x.id === id) || null;
    },
    set(value) {
      return value.id;
    }
  })),
  cropzoneIntersection: types.maybeNull(Feature),
  search: types.maybeNull(types.string),
  useFieldBoundary: false
}).views(self => ({
  get hasFeatures() {
    return self.features && self.features.length > 0;
  },
  get featureObjects() {
    self.features.reduce((acc: any[], cur) => {
      if (isPresent(cur.getPolygon?.coordinates) && cur.selected) {
        acc.push(cur.object);
      }
      return acc;
    }, []);
    return self.features.filter(f => isPresent(f.getPolygon?.coordinates) && f.selected).map(f => f.object);
  },
  get filteredFeatures() {
    if (self.search) {
      const re = new RegExp(`.*${escapeRegExp(self.search.trim().toLowerCase())}.*`);
      return self.features.filter(f => isPresent(f.name) ? re.test(f.name.toString().toLowerCase()) : false);
    }
    return self.features;
  },
  get featuresWithWarning() {
    return self.features.filter(f => !f.withoutWarning && f.canSave).length;
  },
  get featuresWithError() {
    return self.features.filter(f => !f.canSave).length;
  },
  get featuresSelected() {
    return self.features.filter(f => f.selected).length;
  },
  get featuresWithoutError() {
    return self.features.filter(f => f.canSave).length;
  },
  get validBoundaryToSave() {
    return self.useFieldBoundary || self.cropzoneIntersection?.validPolygon;
  }
})).actions(self => ({
  setFeatures(features: {
    properties: {
      [k: string]: string | null | undefined;
    };
  }[], farmId: string) {
    const cleaned = features.map(f => cleanFeature(f));
    self.features.replace(cleaned.map(f => ({
      ...f,
      properties: {
        ...f.properties,
        farm_id: farmId,
        seed_usage_unit: f.properties.seed_usage_unit || 'bags',
        seed_usage: Number(f.properties.seed_usage) || 0
      }
    })));
  },
  loadFeature(feature: LFMapGeoJSON) {
    const loadedFeature = self.features.find(f => f.id === feature.properties.cropzoneID);
    if (loadedFeature) {
      self.selectedFeature = loadedFeature;
      return;
    }
    const id = feature.properties.fieldID || feature.properties.cropzoneID || uuidv4();
    self.features.replace([Feature.create({
      id: id,
      geometry: {
        type: 'GeometryCollection',
        geometries: [feature.geometry]
      },
      properties: {
        id: id,
        name: feature.properties.name,
        amountOfSeeds: feature.properties.amountOfSeeds,
        ...(isPresent(feature.properties.amountOfSeeds_unit) ? {
          amountOfSeeds_unit: seedUnitList[feature.properties.amountOfSeeds_unit]
        } : {})
      }
    })]);
    self.selectedFeature = self.features[0];
  },
  setSelectedFeature(feature: IFeature) {
    self.selectedFeature = feature;
  },
  reset() {
    self.features.clear();
    self.useFieldBoundary = false;
    self.cropzoneIntersection = null;
  },
  setSearch(query: string) {
    self.search = query;
  },
  emptyFeature(drawMode: LFDrawMode) {
    if (drawMode === 'none') return;
    self.features.clear();
    self.features.push(cleanFeature({}, drawMode));
    self.selectedFeature = self.features[0];
    self.selectedFeature?.setSelectedForDraw();
  },
  handleSelectAll(isSelectAll: boolean) {
    self.features.forEach((f: IFeature) => f.handleSelectAll(isSelectAll));
  },
  setCropzoneIntersection(cropzone: olFeature) {
    self.cropzoneIntersection = castToSnapshot(transformFromOlFeature(cropzone));
    return self.cropzoneIntersection;
  },
  clearCropzoneIntersection() {
    self.cropzoneIntersection = null;
  },
  initializeCropzoneIntersection(coordinates: number[][][]) {
    self.cropzoneIntersection = castToSnapshot({
      id: uuidv4(),
      geometry: {
        type: 'GeometryCollection' as const,
        geometries: [{
          type: 'Polygon' as const,
          coordinates
        }]
      },
      properties: {}
    });
  },
  setUseFieldBoundary(isAllOver: boolean) {
    self.useFieldBoundary = isAllOver;
  },
  toggleUseFieldBoundary() {
    self.useFieldBoundary = !self.useFieldBoundary;
  }
})).actions(self => ({
  resetFeature(feature?: LFMapGeoJSON | null) {
    self.features.clear();
    if (feature) {
      return self.loadFeature(feature);
    }
    self.emptyFeature('polygon');
  }
}));
export type IFeatureStore = Instance<typeof FeatureStore>;